import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  payloadNumber,
  payloadBool,
  payloadString,
  payloadArray,
  payloadObject,
} from '../realtime-sync.payload';
import type { RenderBlockReason } from '@/types/render';
import {
  setProgressAnchor,
  getProgressAnchor,
} from '@/shared/lib/progress';
import type { DataChangedEvent } from '../realtime-sync.types';
import { useJobQueueStore } from '@/features/design/job-queue';

export async function handleQueueSnapshot(event: DataChangedEvent): Promise<void> {
  const store = useJobQueueStore.getState();
  const payload = event.payload;

  // Handle both formats: { jobs: [...] } or direct array
  const jobsArray =
    payloadArray(payload, 'jobs') ??
    (Array.isArray(event.payload) ? event.payload : null);

  if (!jobsArray || !Array.isArray(jobsArray)) {
    wsLogger.warn(
      '[RealtimeSync] Invalid queue snapshot payload:',
      event.payload
    );
    return;
  }

  wsLogger.debug('[RealtimeSync] Queue snapshot:', {
    jobsCount: jobsArray.length,
  });

  // Map backend job format to frontend Job type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const queueJobs = jobsArray.map((item: any) => {
    const baseJob = {
      id: item.id,
      status: item.status,
      startedAt: item.started_at,
      durationPredicted: item.duration_predicted,
      attemptNumber: item.attempt_number || 1,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      completedAt: item.completed_at,
      conceptId: item.concept_id,
      projectId: item.project_id,
      conceptName: item.concept_name,
      conceptSequenceNumber: item.concept_sequence_number,
      projectName: item.project_name,
    };

    if (item.job_type === 'album') {
      return {
        ...baseJob,
        jobType: 'album' as const,
        conceptAngleId: null,
        angleType: null,
        albumTemplate: item.album_template,
        albumIncludeBlueprints: item.album_include_blueprints ?? false,
        albumFormat: item.album_format,
        albumLanguage: item.album_language,
        albumFilename: item.album_filename,
        albumPublicUrl: item.album_public_url,
      };
    } else if (item.job_type === 'estimate') {
      return {
        ...baseJob,
        jobType: 'estimate' as const,
        conceptAngleId: null,
        angleType: null,
        estimateCurrency: item.estimate_currency ?? null,
        estimateGrandTotal: item.estimate_grand_total ?? null,
        estimateRegionLabel: item.estimate_region_label ?? null,
      };
    } else if (
      item.job_type === 'upscale' ||
      item.scenario_type === 'upscale'
    ) {
      return {
        ...baseJob,
        jobType: 'upscale' as const,
        scenarioType: 'upscale' as const,
        conceptAngleId: item.concept_angle_id,
        angleType: item.angle_type,
        originalRenderId: item.original_render_id,
        imageUrl: item.image_url,
        angleUrl: item.angle_url,
      };
    } else {
      return {
        ...baseJob,
        jobType: 'render' as const,
        scenarioType: item.scenario_type,
        conceptAngleId: item.concept_angle_id,
        angleType: item.angle_type,
        imageUrl: item.image_url,
        angleUrl: item.angle_url,
        isUpscaled: item.is_upscaled,
      };
    }
  });

  // Create monotonic progress anchors for processing jobs (only if no anchor yet)
  // Uses performance.now() instead of Date.now() to avoid device-server clock skew
  const uptime = performance.now();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jobsArray.forEach((item: any) => {
    if (
      item.status === 'processing' &&
      item.duration_predicted > 0 &&
      !getProgressAnchor(item.id)
    ) {
      // Use server progress field if available, otherwise estimate from wall clock
      let anchorProgress = 0;
      if (typeof item.progress === 'number') {
        anchorProgress = Math.min(item.progress, 95);
      } else if (item.started_at) {
        const elapsed =
          (Date.now() - new Date(item.started_at).getTime()) / 1000;
        anchorProgress = Math.min(
          Math.max((elapsed / item.duration_predicted) * 100, 0),
          95
        );
      }

      setProgressAnchor(item.id, {
        anchorUptime: uptime,
        anchorProgress,
        durationPredicted: item.duration_predicted,
      });
    }
  });

  // Adopt server-authoritative render-slot aggregate when present. Computed once
  // per snapshot in API RenderLimitsService::getQueueStatus so all 3 frontends
  // gate the Generate button from the same value instead of re-aggregating
  // client-side.
  const aggregateRaw = payloadObject(payload, 'aggregate');
  if (aggregateRaw) {
    const queueLimit = payloadNumber(aggregateRaw, 'queue_limit');
    const activeRenders = payloadNumber(aggregateRaw, 'active_renders');
    const availableSlots = payloadNumber(aggregateRaw, 'available_slots');
    const canRender = payloadBool(aggregateRaw, 'can_render');
    // Server-driven render-gate reason; absent/empty → null (render allowed).
    // The preflight maps this to a paywall; clients never recompute it.
    const blockReason =
      (payloadString(aggregateRaw, 'block_reason') as RenderBlockReason) ??
      null;
    if (
      queueLimit !== undefined &&
      activeRenders !== undefined &&
      availableSlots !== undefined &&
      canRender !== undefined
    ) {
      store.setQueueAggregate({
        queueLimit,
        activeRenders,
        availableSlots,
        canRender,
        blockReason,
      });
    }
  }

  store.replaceJobQueue(queueJobs);
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}
